import { type BrandName, type BrandZohoTypes } from "@elit-hair/brands";
import { useLocalStorage } from "@elit-hair/elit-tools";
import { Button, Card, CardContent, CardFooter, Icon, Separator } from "@elit-hair/elit-ui";
import { t } from "i18next";
import { useCallback, useEffect } from "react";
import { TbExternalLink, TbPhoneOff, TbTrash } from "react-icons/tb";

import { LastCallPhoneNumbers } from "@/components/last-call-card/last-call-phone-numbers";
import { LastCallRecordInfo } from "@/components/last-call-card/last-call-record-info";
import { OpenRecordButton } from "@/components/ui/open-record-button";
import { LOCAL_STORAGE_KEYS } from "@/constants/local-storage-keys";
import { useCall } from "@/providers/hooks/use-call";
import type { ModuleType } from "@/types/zoho.types";
import { getBrandFromOrgId } from "@/utils/get-brand-from-org-id";

type LastCallRecord = {
    data: BrandZohoTypes["Leads"] | BrandZohoTypes["Contacts"];
    moduleType: ModuleType;
    orgId: string; // Store the orgId from the original call
    brand?: BrandName;
    callPurpose?: string;
};

export const LastCallCard = () => {
    const { startCall, currentRecordInformation, currentCallData, isCallActive, handleHangupCall, callButtonCooldown } =
        useCall();

    const [lastCallRecord, setLastCallRecord] = useLocalStorage<LastCallRecord | null>(
        LOCAL_STORAGE_KEYS.LAST_CALL.key,
        null
    );

    useEffect(() => {
        if (currentRecordInformation && currentCallData?.orgId) {
            const brand = getBrandFromOrgId(currentCallData.orgId);

            if (currentCallData?.moduleType === "Leads" || currentCallData?.moduleType === "Contacts") {
                setLastCallRecord({
                    data: currentRecordInformation,
                    moduleType: currentCallData.moduleType,
                    orgId: currentCallData.orgId,
                    brand,
                    callPurpose: currentCallData.callPurpose,
                });
            }
        }
    }, [currentRecordInformation, currentCallData]);

    const handleStartCall = useCallback(
        (phoneNumber: string) => {
            if (!lastCallRecord) {
                startCall(phoneNumber);
                return;
            }

            const orgId = lastCallRecord.orgId;

            const calledName = lastCallRecord.data.Full_Name?.trim();
            const calledEmail = lastCallRecord.data.Email?.trim();

            startCall(phoneNumber, {
                moduleType: lastCallRecord.moduleType,
                recordId: lastCallRecord.data.id,
                orgId,
                ...(lastCallRecord.callPurpose ? { callPurpose: lastCallRecord.callPurpose } : {}),
                ...(calledName ? { calledName } : {}),
                ...(calledEmail ? { calledEmail } : {}),
            });
        },
        [startCall, lastCallRecord]
    );

    const clearLastCallRecord = () => {
        setLastCallRecord(null);
    };

    const hasActiveCall = isCallActive();

    return (
        <Card className="h-full w-full bg-transparent border-none">
            <CardContent className="flex flex-col w-full p-0 my-4">
                {lastCallRecord ? (
                    <>
                        <LastCallRecordInfo record={lastCallRecord.data} />
                        <LastCallPhoneNumbers
                            record={lastCallRecord.data}
                            moduleType={lastCallRecord.moduleType}
                            onCall={handleStartCall}
                        />
                    </>
                ) : (
                    <div className="text-center text-muted-foreground py-10 text-sm">{t("no_last_call_data")}</div>
                )}
            </CardContent>
            <Separator />
            <CardFooter className="w-full flex flex-col gap-2">
                {lastCallRecord && (
                    <div className="flex flex-col w-full gap-2 py-2">
                        <div
                            className={`overflow-hidden transition-all duration-300 ease-out ${
                                hasActiveCall
                                    ? "max-h-14 opacity-100 border-b border-secondary pb-2"
                                    : "max-h-0 opacity-0 pointer-events-none"
                            }`}
                        >
                            <Button
                                variant="destructive"
                                onClick={handleHangupCall}
                                disabled={callButtonCooldown}
                                className="w-full"
                            >
                                <Icon icon={TbPhoneOff} /> <span className="text-sm">{t("hang_up")}</span>
                            </Button>
                        </div>
                        <OpenRecordButton
                            id={lastCallRecord.data.id}
                            moduleType={lastCallRecord.moduleType}
                            brand={lastCallRecord.brand}
                            label={
                                <span className="flex items-center gap-2">
                                    {t("open_in_crm")} <Icon icon={TbExternalLink} />
                                </span>
                            }
                            disabled={!lastCallRecord.data.id || !lastCallRecord.moduleType}
                        />
                        <Button variant="ghost" onClick={clearLastCallRecord}>
                            <Icon icon={TbTrash} /> <span className="text-sm">{t("clear_last_call")}</span>
                        </Button>
                    </div>
                )}
            </CardFooter>
        </Card>
    );
};
